import { useState, useEffect, createContext, useContext } from 'react';
import { Amplify, Auth, Hub } from 'aws-amplify';
import { CognitoUser } from '@aws-amplify/auth';
import { type ICredentials, type HubCallback } from '@aws-amplify/core';
import {
  CognitoAccessToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router';

import { FullPageSpinner } from '@futurefit-ai/ffui';

import awsExports from '../aws-exports.json';
import {
  getCognitoAttrFromStorage,
  isExpiredSessionError,
  isTokenFromAzureOIDC,
} from '../utils/auth';
import { UnauthenticatedRoute } from '../utils/route';
import { removeManageUsersFilterSessionItems } from '../utils/sessionStorage';
import i18n, { DEFAULT_LOCALE } from './i18n';
import { LocalStorage, SessionStorage } from '../utils/localStorageKeys';

const GRAPHQL_MESH_ENABLED = true;

const amplifyOptions = {
  // Amplify Auth Configuration
  aws_cognito_region: awsExports.region,
  aws_user_pools_id: awsExports.pool_id,
  aws_user_pools_web_client_id: awsExports.client_id,
  oauth: {
    redirectSignIn: `${window.location.origin}/`,
    redirectSignOut: `${window.location.origin}/`,
    responseType: 'token',
    domain: `${awsExports.cognito_domain}.auth.${awsExports.region}.amazoncognito.com`,
  },
};

export const configureAmplify = async () => {
  const tenantClientAppId = await getTenantAppClient();

  const options = tenantClientAppId
    ? {
        ...amplifyOptions,
        aws_user_pools_web_client_id: tenantClientAppId,
      }
    : amplifyOptions;

  // Set the client ID in local storage for future use
  localStorage.setItem(
    LocalStorage.CognitoAppClientId,
    options.aws_user_pools_web_client_id
  );
  Amplify.configure(options);
};

export interface IUser {
  username: string;
  email: string;
  tenantId: string;
  userId: string;
  token?: CognitoAccessToken;
  cognitoUser?: CognitoUser;
  error?: string;
}

export interface ICognitoUser {
  username: string;
  password: string;
  attributes: {
    email: string;
  };
  clientMetadata: {
    tenant_code: string;
    invite_code: string;
    email_consent: boolean;
    locale: string;
  };
}

type SignOutParams = {
  tenantId?: string;
  tenantCode?: string;
  onSignOut?: () => void;
  isHideIntercomEnabled?: boolean;
};

export interface IConfirmUser {
  username: string;
  clientMetadata: {
    tenant_code: string;
    locale: string;
  };
}
export interface IResetUser extends IConfirmUser {
  code: string;
  new_password: string;
}

interface IAuthContext {
  isLoading: boolean;
  user: IUser;
  signUp: ({
    username,
    password,
    attributes,
    clientMetadata,
  }: ICognitoUser) => Promise<any>;
  signIn(
    username: string,
    password: string,
    clientMetadata?: { tenant_code?: string }
  ): Promise<CognitoUser>;
  signOut(params: SignOutParams): Promise<any>;
  confirmSignUp(
    username: string,
    confirmationCode: string,
    tenantCode?: string,
    inviteCode?: string,
    emailConsent?: boolean
  ): Promise<any>;
  resendSignUp(
    username: string,
    clientMetadata: { tenant_code?: string; invite_code?: string }
  ): Promise<any>;
  forgotPassword({ username, clientMetadata }: IConfirmUser): Promise<any>;
  forgotPasswordSubmit({
    username,
    code,
    new_password,
    clientMetadata,
  }: IResetUser): Promise<any>;
  completeNewPassword(user: CognitoUser, newPassword: string): Promise<void>;
  refreshCache: () => Promise<void>;
  getSession(): Promise<CognitoUserSession | null>;
  checkExistence: (email: string) => Promise<boolean>;
  isOnboarded: boolean;
  federatedSignIn: (ssoProvider?: string) => Promise<ICredentials>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<any>;
}

const signIn = (
  username: string,
  password: string,
  clientMetadata?: { tenant_code?: string }
): Promise<CognitoUser> =>
  Auth.signIn(username, password, clientMetadata)
    .then((user: CognitoUser) => {
      Sentry.setUser({ username: user.getUsername() });
      return user;
    })
    .catch((e) => {
      throw e;
    });

const signUp = ({
  username,
  password,
  attributes,
  clientMetadata,
}: ICognitoUser): Promise<any> =>
  Auth.signUp({
    username,
    password,
    attributes,
    clientMetadata: {
      tenant_code: clientMetadata.tenant_code || '',
      invite_code: clientMetadata.invite_code || '',
      email_consent: (clientMetadata.email_consent || false).toString(),
      locale: clientMetadata.locale,
    },
  }).catch((e) => {
    throw e;
  });

const signOut = async ({
  onSignOut,
  tenantId,
  tenantCode,
  isHideIntercomEnabled,
}: SignOutParams): Promise<any> => {
  tenantCode &&
    sessionStorage.setItem(SessionStorage.TempTenantCode, tenantCode);

  // Amplify Auth SignOut is not able to clear user session for federated sign-in.
  // Ref: https://github.com/aws-amplify/amplify-js/issues/1583
  const localStorageKeys = Object.keys(localStorage);
  localStorageKeys.forEach((key) => {
    if (key.includes('CognitoIdentityServiceProvider')) {
      localStorage.removeItem(key);
    }
  });

  // For immediate logout for API access, due to Cognito JWT lifetime.
  addTokenToRevokedList();

  await Auth.signOut();
  Sentry.configureScope((scope) => scope.setUser(null));
  // Stops intercom and clears current session
  (window as any).Intercom('shutdown');
  // Re=initialize Intercom with fresh settings
  (window as any).Intercom('boot', {
    app_id: import.meta.env.VITE_INTERCOM_APPID,
    tenant_id: tenantId,
    hide_default_launcher: isHideIntercomEnabled ?? false,
    language_override: (i18n.language ?? DEFAULT_LOCALE).substring(0, 2),
  });
  onSignOut && onSignOut();
};

/**
 * Add the token to the revoked token list in access control.
 * We do this to block API calls immediately after logout: the Cognito JWT cannot be invalidated immediately.
 */
const addTokenToRevokedList = async () => {
  try {
    const session = await getSession();
    const response = await fetch(`${import.meta.env.VITE_GRAPHQL_HOST}`, {
      method: 'POST',
      headers: {
        Authorization: `${session?.getAccessToken().getJwtToken()}`,
        'Content-type': 'application/json',
      },
      body: JSON.stringify({
        variables: {
          token: `${session?.getAccessToken().getJwtToken()}`,
        },
        query: `
        mutation RevokeToken($token: String) {
          revokeToken(token: $token)
        }
        `,
      }),
    });
    const responseJson = await response.json();
    responseJson.errors && console.error(responseJson.errors);
  } catch (error) {
    // Do not prevent logout in the event of revokeToken failure.
    console.error('Error calling revokeToken', error);
  }
};

/**
 * Fetch tenant app client ID from the tenant code.
 * Scenario0: If the user is redirected to /sso, use the client ID from the URL.
 * Scenario1: If tenant code is provided, fetch the tenant app client ID from the tenant code.
 * Scenario2: If tenant code is not provided, use the last used client ID from the local storage.
 * Scenario3: If tenant code is not provided and the last used client ID is not found in the local storage, use the default client ID.
 *
 * @returns tenant app client ID
 */
const getTenantAppClient = async (): Promise<string | undefined> => {
  try {
    // Dynamically determines the client id for the current sso flow
    // Note: we assume this will only be triggered via Cognito hosted UI
    // so regular users will never hit this route.
    if (
      // The callback url will be of the form /sso/:clientId
      // which is captured in this condition.
      // This also stores the client id in local storage.
      window.location.pathname.includes('/sso/') &&
      window.location.pathname.split('/').filter((s) => s).length > 1
    ) {
      const ssoClientIdInUrl =
        window.location.pathname.split('/').filter((s) => s)[1] ?? '';
      return ssoClientIdInUrl;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const tenantCode = urlParams.get('tc') || '';
    const tenantCodeFromSession = sessionStorage.getItem(
      SessionStorage.TempTenantCode
    );
    if (!tenantCode || tenantCode === tenantCodeFromSession) {
      // console.warn('Tenant code is missing. Using last used client ID');
      // This could be due to a direct navigation to the sign-in page or
      // user just refreshing some post auth routes.
      return (
        localStorage.getItem(LocalStorage.CognitoAppClientId) ??
        awsExports.client_id
      );
    }

    const getTenantResponse = await fetch(
      `${import.meta.env.VITE_GRAPHQL_HOST}`,
      {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            query GetTenant($tenantCode: String!) {
              getTenant(tenant_id: $tenantCode) {
                configuration_id
              }
            }`,
          variables: {
            tenantCode,
          },
        }),
      }
    );
    const json = await getTenantResponse.json();
    const configurationId = json.data.getTenant?.configuration_id;

    if (!configurationId) {
      throw new Error('Configuration ID is missing');
    }

    const getTenantConfigurationResponse = await fetch(
      `${import.meta.env.VITE_GRAPHQL_HOST}`,
      {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          query: `
      query GetTenantConfiguration($configuration_id: String!) {
        getTenantConfiguration(configuration_id: $configuration_id) {
          sso_configuration {
            app_client_id
          }
        }
      }`,
          variables: {
            configuration_id: configurationId,
          },
        }),
      }
    );
    const json2 = await getTenantConfigurationResponse.json();
    const appClient =
      json2.data.getTenantConfiguration.sso_configuration?.app_client_id;
    if (!appClient) {
      // console.warn('Tenant does not have SSO app_client_id. Using default client ID');
      // we cannot use the client_id from local storage as it may be from a different tenant
      return awsExports.client_id;
    }
    return appClient;
  } catch (error) {
    console.error('Error fetching tenant app client', error);
    return (
      localStorage.getItem(LocalStorage.CognitoAppClientId) ??
      awsExports.client_id
    );
  }
};

const federatedSignIn = (ssoProvider?: string) => {
  return Auth.federatedSignIn(
    ssoProvider ? { customProvider: ssoProvider } : undefined
  );
};

const confirmSignUp = (
  username: string,
  confirmationCode: string,
  tenantCode?: string,
  inviteCode?: string,
  emailConsent?: boolean
): Promise<any> =>
  Auth.confirmSignUp(username, confirmationCode, {
    clientMetadata: {
      tenant_code: tenantCode || '',
      invite_code: inviteCode || '',
      email_consent: (emailConsent ?? true).toString(),
    },
  }).then(
    () =>
      (window.document.location = tenantCode
        ? `/sign-in?tc=${tenantCode}&ic=${inviteCode}`
        : '/sign-in')
  );

const resendSignUp = (
  username: string,
  clientMetadata: { tenant_code?: string; invite_code?: string; locale: string }
): Promise<any> =>
  Auth.resendSignUp(username, clientMetadata).catch((e) => {
    throw e;
  });

const completeNewPassword = async (
  user: CognitoUser,
  newPassword: string
): Promise<void> => {
  return Auth.completeNewPassword(user, newPassword);
};

const forgotPassword = ({
  username,
  clientMetadata,
}: IConfirmUser): Promise<any> =>
  Auth.forgotPassword(username, clientMetadata)
    .then(() => {
      const tenantCode = clientMetadata.tenant_code;
      window.document.location = tenantCode
        ? `/reset-password?tc=${tenantCode}&u=${encodeURIComponent(username)}`
        : `/reset-password?u=${encodeURIComponent(username)}`;
    })
    .catch((e) => {
      if (e.code === 'UserLambdaValidationException') {
        throw e;
      }

      // Security: do not indicate if email exists.
      const tenantCode = clientMetadata.tenant_code;
      window.document.location = tenantCode
        ? `/reset-password?tc=${tenantCode}&u=${encodeURIComponent(username)}`
        : `/reset-password?u=${encodeURIComponent(username)}`;
    });

const forgotPasswordSubmit = ({
  username,
  code,
  new_password,
  clientMetadata,
}: IResetUser): Promise<any> =>
  Auth.forgotPasswordSubmit(username, code, new_password, clientMetadata)
    .then(() => {
      window.document.location = clientMetadata.tenant_code
        ? `/sign-in?tc=${clientMetadata.tenant_code}`
        : '/sign-in';
    })
    .catch((e) => {
      throw e;
    });

const refreshCache = async () => {
  await Auth.currentAuthenticatedUser({ bypassCache: true });
};

const getSession = (): Promise<CognitoUserSession | null> =>
  Auth.currentSession();

const changePassword = (
  oldPassword: string,
  newPassword: string
): Promise<any> =>
  Auth.currentAuthenticatedUser()
    .then((user) => {
      return Auth.changePassword(user, oldPassword, newPassword);
    })
    .catch((err) => {
      throw err;
    });

/**
 * Check if an email already exists in our Cognito user pool.
 * Utilize the Cognito error got from fake sign-in to determine whether an email exists (unauthorized, required pw reset), or doesn't exist (not found, not confirmed) in our Cognito user pool.
 * @param email
 * @returns boolean // whether email already exists in our Cognito user pool
 */
const checkExistence = (email: string): Promise<boolean> => {
  return Auth.signIn(email.toLowerCase(), '123')
    .then(() => {
      return false;
    })
    .catch((error) => {
      const code = error.code;
      switch (code) {
        case 'NotAuthorizedException':
        case 'PasswordResetRequiredException':
          return true;
        case 'UserNotFoundException':
        case 'UserNotConfirmedException':
        default:
          return false;
      }
    });
};

/**
 * Empty user object to replicate a null value.
 * This is used just to make `user` always defined so that
 * we can access its attributes more conveniently
 * @example
 * const { user: { tenantId, userId } } = useAuth();
 * const f = tenantId // this is of type `string` ✅
 *
 * // INSTEAD OF
 *
 * const { user } = useAuth();
 * const f = user?.tenantId // this is of type `string | undefined` 🤮
 */
const emptyUser: IUser = { email: '', tenantId: '', userId: '', username: '' };

export const useCognito = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<IUser>(emptyUser);
  const [isLoading, setIsLoading] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(false);

  /*
   * When a page is redirected, the original (authenticated) url can be identified via a `location.key = 'default'` and its pathname.
   *
   * This if-statement determines whether this unauthetnicated user is redirected to sign-in page from an authenticated url.
   * If yes, we will store this in local storage, which will be used for later in SignIn functions.
   */
  useEffect(() => {
    if (
      location.key === 'default' &&
      location.pathname !== UnauthenticatedRoute.SIGN_IN
    ) {
      window.localStorage.setItem('redirectedFrom', location.pathname);
    }
  }, []);

  const loadUserProfile = async () => {
    let session;
    try {
      setIsLoading(true);
      session = await getSession();

      if (session && session.isValid()) {
        const userInfo = await Auth.currentUserInfo();
        const accessToken = session.getAccessToken();

        if (!accessToken.payload['cognito:groups']) {
          await Auth.signOut();
          setUser({ ...emptyUser, error: 'MissingUserGroup' });
          setIsLoading(false);
          return;
        }

        setUser({
          email: userInfo.attributes.email,
          username: userInfo.username,
          userId: accessToken.payload.sub,
          tenantId: JSON.parse(accessToken.payload['cognito:groups'][0])
            .tenant_id,
          token: accessToken,
        });
      } else if (isTokenFromAzureOIDC(session?.getIdToken().payload)) {
        Auth.federatedSignIn();
      }
    } catch (e: any) {
      setIsLoading(false);

      if (isExpiredSessionError(e.message)) {
        const idToken = getCognitoAttrFromStorage('idToken');

        if (isTokenFromAzureOIDC(idToken)) {
          Auth.federatedSignIn();
        }
      } else {
        session = await getSession();

        if (isTokenFromAzureOIDC(await session?.getIdToken().payload)) {
          Auth.federatedSignIn();
        }
      }

      return;
    }

    try {
      const appSyncHost = `${import.meta.env.VITE_GRAPHQL_HOST}`.split('/')[2];
      const response = await fetch(`${import.meta.env.VITE_GRAPHQL_HOST}`, {
        method: 'POST',
        headers: {
          Authorization: `${session?.getAccessToken().getJwtToken()}`,
          host: appSyncHost, // the middle bits of the URL
          'Content-type': 'application/json;charset=UTF-8',
        },
        body: JSON.stringify({
          variables: {
            locale: 'en-US',
            user_uuid: session?.getAccessToken().payload.sub, //
          },
          query: !GRAPHQL_MESH_ENABLED
            ? `
              query GetUserProfile($locale: String!, $user_uuid: String!) {\n  getUserProfile(query: {locale: $locale, user_uuid: $user_uuid}) {\n    is_onboarded\n    user_uuid\n    __typename\n  }\n}
              `
            : `
              query GetUserProfile($locale: String!, $user_uuid: String!) {\n  getUserProfile(getProfileInput: {locale: $locale, user_uuid: $user_uuid}) {\n    is_onboarded\n    user_uuid\n    __typename\n  }\n}
              `,
        }),
      });
      const json = await response.json();
      setIsOnboarded(json.data.getUserProfile.is_onboarded || false);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  /**
   * If an authenticated user signing in via a url redirection from an authenticated url, this user will be navigated to the original authenticated url (that he/ she was planning to navigate to) upon signing in successfully.
   *
   * If an authenticated user is not signing in via url redirection, this user will navigate into the the root authenticated page as usual.
   */
  const signInUrlRedirection = () => {
    const redirectedFrom = window.localStorage.getItem('redirectedFrom');
    if (redirectedFrom) {
      navigate(redirectedFrom);
    } else {
      navigate('/');
    }
  };

  const authListener: HubCallback = async ({ payload: { event, data } }) => {
    switch (event) {
      case 'signIn': {
        const accessToken = data.signInUserSession.accessToken;

        await setUser({
          username: data.username,
          tenantId: JSON.parse(accessToken.payload['cognito:groups'][0])
            .tenant_id,
          userId: accessToken.payload.sub,
          token: accessToken,
          email: data.attributes.email,
        });
        loadUserProfile();
        signInUrlRedirection();
        break;
      }
      case 'signUp':
        window.localStorage.removeItem('emailConsent');
        break;
      case 'signOut':
        setUser(emptyUser);
        window.localStorage.removeItem('redirectedFrom');
        removeManageUsersFilterSessionItems();
        break;
    }
  };

  useEffect(() => {
    loadUserProfile();
  }, []);

  useEffect(() => {
    const cancel = Hub.listen('auth', authListener);
    return () => cancel();
  }, []);

  return {
    isLoading,
    user,
    signUp,
    signIn,
    signOut,
    confirmSignUp,
    resendSignUp,
    forgotPassword,
    forgotPasswordSubmit,
    completeNewPassword,
    getSession,
    checkExistence,
    refreshCache,
    isOnboarded,
    changePassword,
    federatedSignIn,
    loadUserProfile,
  };
};

export const AuthContext = createContext<IAuthContext>({
  isLoading: false,
  user: emptyUser,
  signUp,
  signIn,
  signOut,
  confirmSignUp,
  resendSignUp,
  forgotPassword,
  getSession,
  forgotPasswordSubmit,
  completeNewPassword,
  isOnboarded: false,
  changePassword,
  checkExistence,
  refreshCache,
  federatedSignIn,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const auth = useCognito();
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState('');

  useRefreshOnCognitoUserChange(clientId);

  const awaitConfigureAmplify = async () => {
    await configureAmplify();
    setLoading(false);
    await auth.loadUserProfile();
  };

  useEffect(() => {
    awaitConfigureAmplify();
  }, []);

  useEffect(() => {
    if (!loading && auth.user.userId) {
      // When a user is signed in, we need to get the client ID from the user pool
      Auth.currentUserPoolUser().then((userInfo) => {
        setClientId(userInfo.pool.clientId);
      });
    } else {
      // To ensure that the client ID is reset when the user signs out
      setClientId('');
    }
  }, [loading, clientId, auth.user.userId]);

  if (loading) {
    return <FullPageSpinner />;
  }

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook that listens for changes in the Cognito user and refreshes the page if a different user signs in.
 *
 * @param {string} clientIdProp - The client ID used to identify the Cognito user.
 *
 * @returns {void}
 *
 * @remarks
 * This hook is used in the `AuthProvider` component to listen for changes in the Cognito user and refresh the page if a different user signs in.
 *
 * The reason for this hook is an error that occurs when a user with multiple tabs open to Profile (A) signs out of one of those tabs.
 * If that user signs out of Profile (A) on one tab, then signs in with a different Profile (B), the other tabs that are still open to Profile (A)
 * will refresh their tokens to Profile (B)'s token once their tokens expire. Since the tabs with Profile (a) are still opened and have not been refreshed,
 * the user will still see Profile (A) data, and can use Profile (A)'s data to send update requests with the newly refreshed token of Profile (B), thereby saving Profile (A)s
 * data to Profile (B) [possibly cross tenant]. This hook is used to refresh the page when a different user signs in to prevent this error.
 *
 * This hook sets up three `useEffect` hooks:
 * 1. The first `useEffect` sets the client ID and reloads the page if the client ID changes (SSO tenants use a custom client ID).
 * 2. The second `useEffect` listens for changes in the local storage set by Cognito to detect if the user has signed in with a different account.
 * 3. The third `useEffect` checks if the user email has changed and reloads the page if a different user is detected.
 *
 * @example
 * ```tsx
 * const clientId = 'your-client-id';
 * useRefreshOnCognitoUserChange(clientId);
 * ```
 */
const useRefreshOnCognitoUserChange = (clientIdProp: string) => {
  const localStorageKey = `CognitoIdentityServiceProvider.${clientIdProp}.LastAuthUser`;

  const [
    lastSignedInUserFromCognitoLocalStorage,
    setLastSignedInUserFromCognitoLocalStorage,
  ] = useState(() => localStorage.getItem(localStorageKey) || '');
  const [userEmail, setUserEmail] = useState('');
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    if (clientIdProp && !clientId) {
      setClientId(clientIdProp);
    } else if (clientIdProp && clientId && clientIdProp !== clientId) {
      // for cases when a tenant configuration has a custom client ID (SC with custom SSO for example)
      // if the client ID prop has changed then that means the localStorage listener would not work for the new client ID
      // so we need to reload the page since that means a different user has signed in with a different client ID
      !window.isCypressEnv /*cypress seems to sometimes trigger this by accident during set up so  we will only refresh for non-cypress environments */ &&
        window.location.replace('/');
    }
  }, [clientIdProp, clientId]);

  useEffect(() => {
    if (clientId) {
      // This use effect listens for changes in the local storage to detect if the user has signed in with a different account

      const handleStorageChange = () => {
        setLastSignedInUserFromCognitoLocalStorage(
          localStorage.getItem(localStorageKey) || ''
        );
      };

      window.addEventListener('storage', handleStorageChange);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, [clientId]);

  useEffect(() => {
    if (lastSignedInUserFromCognitoLocalStorage) {
      if (userEmail && userEmail !== lastSignedInUserFromCognitoLocalStorage) {
        // if the local storage has a different user than the one currently signed in then reload the page
        !window.isCypressEnv /*cypress seems to sometimes trigger this by accident during set up so  we will only refresh for non-cypress environments */ &&
          window.location.replace('/');
      } else if (!userEmail) {
        // if the user email is not set then the user is just signing in so set the email
        setUserEmail(lastSignedInUserFromCognitoLocalStorage);
      }
    }
  }, [lastSignedInUserFromCognitoLocalStorage, userEmail]);
};
